import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ChangePasswordRequestSchema, type ChangePasswordRequest } from '@bliks-io/contracts';
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogShell,
  DialogShellFooter,
  DialogShellHeader,
  Skeleton
} from '@bliks-io/ui';
import { FormInput } from '@/components/form/rhf';
import { useChangePassword } from '@/api/mutations/useProfile';
import { useAccountSecurity } from '@/api/queries/useAccountSecurity';
import { LoadErrorRetry } from '@/components/load-error-retry/LoadErrorRetry';
import { TwoFactorSection } from './TwoFactorSection';
import { PasskeySection } from './PasskeySection';
import {
  clearPendingSensitiveAction,
  peekPendingSensitiveAction,
  useStepUpReauth,
  type StepUpAction
} from '@/lib/auth/stepUpReauth';
import { Building2, Info, ShieldCheck } from 'lucide-react';

interface SecurityTabProps {
  onSuccess?: () => void;
}

/** The tab's one-line "nothing to do here, and here is why" panel. */
function InfoNotice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <Info className="h-5 w-5 text-primary mt-0.5" />
        <p className="text-sm">{children}</p>
      </div>
    </div>
  );
}

/**
 * Security Tab Component
 *
 * Drives the whole self-service security surface from the account-security
 * status endpoint:
 * - 'enabled'     — password change form, plus TOTP 2FA when available
 * - 'external'    — credentials and MFA live at the organization's IdP
 * - 'unavailable' — no linked identity yet; nothing can be changed here
 */
export function SecurityTab({ onSuccess }: SecurityTabProps) {
  const { t } = useTranslation();
  const { data: security, isLoading, isFetching, isError, refetch } = useAccountSecurity();
  // Peek during render, clear only after commit: a discarded render (aborted
  // transition, StrictMode) must not consume the marker. Single read point —
  // the two sections below must not race the sessionStorage.
  const [pendingAction] = useState(() => peekPendingSensitiveAction());
  useEffect(function consumePendingActionOnce() {
    clearPendingSensitiveAction();
  }, []);
  // The action the step-up dialog was opened for; null = dialog closed.
  const [stepUpPrompt, setStepUpPrompt] = useState<StepUpAction | null>(null);
  const {
    beginStepUp,
    isStarting: isStartingStepUp,
    isRequesting: isRequestingStepUp,
    resetRedirect: resetStepUpRedirect
  } = useStepUpReauth();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError || !security) {
    return (
      <LoadErrorRetry
        message={t('userSettings.security.statusError')}
        loading={isFetching}
        onRetry={() => void refetch()}
      />
    );
  }

  if (security.passwordChange === 'external') {
    return (
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-primary mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('userSettings.security.ssoManagedTitle')}</p>
            <p className="text-xs text-muted-foreground">
              {t('userSettings.security.ssoManagedDescription', {
                idpName: security.ssoIdpName ?? t('userSettings.security.ssoManagedFallbackIdp')
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (security.passwordChange === 'unavailable') {
    return <InfoNotice>{t('userSettings.security.passwordUnavailable')}</InfoNotice>;
  }

  const requestStepUp = (action: StepUpAction) => setStepUpPrompt(action);

  // Freshness gate for every sensitive affordance: a stale session goes to
  // the step-up dialog instead of firing a guaranteed-403 mutation. The
  // mutations' onReauthRequired 403 handling stays as backstop.
  const guardSensitive = (action: StepUpAction, run: () => void) => {
    if (security.reauthRequired) {
      requestStepUp(action);
      return;
    }
    run();
  };

  const confirmStepUp = () => {
    if (stepUpPrompt === null) return;
    void beginStepUp(stepUpPrompt === 'passkey-add' ? undefined : stepUpPrompt);
  };

  const stepUpDescription =
    stepUpPrompt === 'passkey-add'
      ? t('userSettings.security.stepUpPasskeyDescription')
      : t('userSettings.security.stepUpDescription');

  const closeStepUpPrompt = () => {
    setStepUpPrompt(null);
    resetStepUpRedirect();
  };

  return (
    <div className="space-y-6">
      {/* A change needs the current password, which an account without a
          password credential can never supply — the rest of the surface
          (TOTP, passkeys) stays available. */}
      {security.hasPassword ? (
        <PasswordChangeForm onSuccess={onSuccess} />
      ) : (
        <InfoNotice>{t('userSettings.security.noPasswordSet')}</InfoNotice>
      )}
      {security.totp.available && (
        <TwoFactorSection
          totp={security.totp}
          pendingAction={pendingAction}
          guardSensitive={guardSensitive}
          onStepUpRequired={requestStepUp}
        />
      )}
      {security.passkeys.available && (
        <PasskeySection
          passkeys={security.passkeys}
          pendingAction={pendingAction}
          guardSensitive={guardSensitive}
          onStepUpRequired={requestStepUp}
        />
      )}
      {security.stepUpAvailable ? (
        <ConfirmDialog
          open={stepUpPrompt !== null}
          onOpenChange={(open) => {
            if (!open) closeStepUpPrompt();
          }}
          icon={<ShieldCheck className="h-5 w-5" />}
          title={t('userSettings.security.stepUpTitle')}
          description={stepUpDescription}
          confirmLabel={t('userSettings.security.reauthenticate')}
          onConfirm={confirmStepUp}
          isPending={isRequestingStepUp}
          isConfirmLoading={isStartingStepUp}
        />
      ) : (
        // Dead end: step-up would fail AUTH_NOT_ZITADEL_LINKED (legacy token
        // on a zitadel-mode account), so there is nothing to confirm — a
        // single Close beats a Cancel/Close pair that both dismiss.
        <Dialog
          open={stepUpPrompt !== null}
          onOpenChange={(open) => {
            if (!open) closeStepUpPrompt();
          }}
        >
          <DialogShell size="sm">
            <DialogShellHeader
              icon={<ShieldCheck className="h-5 w-5" />}
              title={t('userSettings.security.stepUpTitle')}
              subtitle={t('userSettings.security.stepUpSignInAgain')}
              onClose={closeStepUpPrompt}
            />
            <DialogShellFooter
              secondary={{ label: t('common.close'), onClick: closeStepUpPrompt }}
            />
          </DialogShell>
        </Dialog>
      )}
    </div>
  );
}

function PasswordChangeForm({ onSuccess }: SecurityTabProps) {
  const { t } = useTranslation();
  const changePassword = useChangePassword();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting }
  } = useForm<ChangePasswordRequest>({
    resolver: zodResolver(ChangePasswordRequestSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (data: ChangePasswordRequest) => {
    await changePassword.mutateAsync(data);
    // Reset form on successful password change
    reset();
    onSuccess?.();
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Info Banner */}
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('userSettings.security.passwordRequirements')}</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>{t('userSettings.security.minLength')}</li>
              <li>{t('userSettings.security.requireLowercase')}</li>
              <li>{t('userSettings.security.requireUppercase')}</li>
              <li>{t('userSettings.security.requireNumber')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Password Fields */}
      <div className="space-y-4">
        <FormInput
          control={control}
          name="currentPassword"
          label={t('userSettings.security.currentPassword')}
          placeholder={t('userSettings.security.currentPassword')}
          type="password"
          required
          autoComplete="current-password"
        />

        <FormInput
          control={control}
          name="newPassword"
          label={t('userSettings.security.newPassword')}
          placeholder={t('userSettings.security.newPassword')}
          type="password"
          required
          autoComplete="new-password"
        />

        <FormInput
          control={control}
          name="confirmPassword"
          label={t('userSettings.security.confirmPassword')}
          placeholder={t('userSettings.security.confirmPassword')}
          type="password"
          required
          autoComplete="new-password"
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <Button type="submit" loading={isSubmitting || changePassword.isPending}>
          {t('userSettings.security.changePassword')}
        </Button>
      </div>
    </form>
  );
}
